import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './MobileLayout.css';

// Simple SVG icons (no external dep needed for these few)
const IconHome    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const IconVideo   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="15" height="10" rx="2"/><polyline points="17 10 22 7 22 17 17 14"/></svg>;
const IconUser    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconMic     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 013 3v8a3 3 0 01-6 0V4a3 3 0 013-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>;
const IconLogout  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
const IconFilm    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2"/><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/></svg>;
const IconReceipt = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>;
const IconChevron = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;
const IconMenu    = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const NAV_ITEMS = [
  { path: '/m',           label: '首页',      icon: IconHome  },
  { path: '/m/generate',  label: '生成视频',  icon: IconVideo },
  { path: '/m/avatars',   label: '我的形象',  icon: IconUser  },
  { path: '/m/voices',    label: '我的声音',  icon: IconMic   },
  { path: '/m/tasks',     label: '我的视频',  icon: IconFilm  },
  { path: '/m/bills',     label: '积分明细',  icon: IconReceipt },
];

/**
 * MobileLayout — wraps every /m/* page.
 *
 * Props:
 *   title       — page title shown in header center  (string)
 *   showBack    — show ← back button instead of hamburger (bool, default false)
 *   onBack      — optional custom back handler; defaults to navigate(-1)
 *   credits     — credits value to display in header right (number|null)
 *   hideHeader  — hide header entirely (bool)
 */
export default function MobileLayout({ children, title = 'PSYCHE-AI', showBack = false, onBack, credits, hideHeader = false }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout } = useAuth();
  const phone = user?.phone || '';
  const maskedPhone = phone.length >= 11
    ? `${phone.slice(0, 3)}****${phone.slice(-4)}`
    : phone || '未登录';
  const navigate = useNavigate();
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleBack = () => {
    if (onBack) { onBack(); return; }
    navigate(-1);
  };

  const handleLogout = async () => {
    setDrawerOpen(false);
    await logout();
    navigate('/m/login', { replace: true });
  };

  const isActive = (path) => {
    if (path === '/m') return location.pathname === '/m' || location.pathname === '/m/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="m-layout">
      {/* Header */}
      {!hideHeader && (
        <header className="m-header">
          {showBack ? (
            <button className="m-header-back" onClick={handleBack} aria-label="返回">
              <IconChevron />
            </button>
          ) : (
            <button
              className="m-header-hamburger"
              onClick={() => setDrawerOpen(true)}
              aria-label="打开菜单"
            >
              <IconMenu />
            </button>
          )}

          <div className="m-header-title">{title}</div>

          <div className="m-header-right">
            {credits != null && (
              <span className="m-header-credits">{credits}</span>
            )}
          </div>
        </header>
      )}

      {/* Main content */}
      <main className="m-layout-main">
        {children}
      </main>

      {/* Drawer overlay */}
      {drawerOpen && (
        <div className="m-drawer-overlay" onClick={() => setDrawerOpen(false)} />
      )}

      {/* Drawer */}
      {drawerOpen && (
        <nav className="m-drawer" aria-label="导航菜单">
          <div className="m-drawer-head">
            <div className="m-drawer-head-top">
              <div className="m-drawer-logo">
                <img src="/logo-mobile-mark.svg" alt="logo" />
                <span className="m-drawer-logo-name">PSYCHE-AI</span>
              </div>
              <button className="m-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="关闭菜单">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="m-drawer-user">{maskedPhone}</div>
            {credits != null && (
              <div className="m-drawer-credits-row">
                <span className="m-drawer-credits-label">可用积分</span>
                <span className="m-drawer-credits-val">{credits}</span>
              </div>
            )}
          </div>

          <div className="m-drawer-nav">
            {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`m-drawer-nav-item${isActive(path) ? ' active' : ''}`}
                onClick={() => setDrawerOpen(false)}
              >
                <Icon />
                {label}
              </Link>
            ))}
          </div>

          <div className="m-drawer-footer">
            <button className="m-drawer-logout" onClick={handleLogout}>
              <IconLogout />
              退出登录
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
